import { Link } from "react-router-dom";
import { Clock, CheckCircle2, AlertTriangle, Phone, ArrowRight, Calendar } from "lucide-react";
import { SiteLayout } from "@/components/site/SiteLayout";
import { PageSEO } from "@/components/site/PageSEO";
import { ResourceHero } from "@/components/site/ResourceHero";
import { QuickAnswer } from "@/components/site/QuickAnswer";
import { ReviewedBy } from "@/components/site/ReviewedBy";
import { CTASection } from "@/components/site/CTASection";
import { brand, founder } from "@/data/site/brand";
import { stockImages } from "@/data/site/stockImages";

const lastReviewed = "2026-06-12";
const datePublished = "2026-06-12";

const QUICK_ANSWER =
  "Most IRS tax resolution cases close in 3 to 9 months. Wage garnishment and bank levy releases can be obtained in 24 to 72 hours once a CPA files Form 2848 and proposes a resolution. Installment agreements typically take 30 to 90 days. Offers in Compromise are the longest — usually 6 to 12 months from submission to IRS decision, and longer if the case goes to Appeals. Audits run 3 to 18 months depending on type. The single biggest accelerator is filing Form 2848 Power of Attorney early so the IRS deals with your representative instead of you.";

const timelineRows: {
  type: string;
  total: string;
  phases: string;
}[] = [
  {
    type: "Wage garnishment release",
    total: "24 – 72 hours",
    phases: "Day 1: Form 2848 + transcript pull + Form 433-F. Day 1–3: ACS or Revenue Officer call → levy release fax to employer.",
  },
  {
    type: "Bank levy release",
    total: "1 – 14 days",
    phases: "Critical window: bank holds funds 21 days from levy date. Faster release = more funds returned.",
  },
  {
    type: "Streamlined installment agreement (<$50k owed, current on filings)",
    total: "Same day – 30 days",
    phases: "Online Payment Agreement portal or single ACS call. Approval is essentially automatic if eligible.",
  },
  {
    type: "Non-streamlined installment agreement (>$50k or non-compliant)",
    total: "30 – 90 days",
    phases: "Transcript pull (1 wk) → Form 433-A/F prep (1–2 wk) → ACS or RO negotiation (2–8 wk).",
  },
  {
    type: "Currently Not Collectible (CNC / Status 53)",
    total: "30 – 90 days",
    phases: "Hardship documentation, Form 433-F submission, IRS review of allowable living expenses.",
  },
  {
    type: "First-Time Penalty Abatement (single year)",
    total: "Same day – 4 weeks",
    phases: "Often granted on the initial ACS call if the 3-year clean-history rule is met.",
  },
  {
    type: "Reasonable Cause penalty abatement",
    total: "60 – 180 days",
    phases: "Written narrative + supporting documents. IRS Form 843 review can be slow.",
  },
  {
    type: "Offer in Compromise (OIC) — lump-sum",
    total: "6 – 9 months",
    phases: "Prep (4–8 wk) → submission → IRS processability check (4 wk) → Examiner review (3–6 mo).",
  },
  {
    type: "Offer in Compromise — periodic payment",
    total: "9 – 14 months",
    phases: "Same workflow as lump-sum, but taxpayer makes monthly offer payments during review.",
  },
  {
    type: "Offer in Compromise — appealed",
    total: "12 – 24 months",
    phases: "Adds 3–9 months for IRS Appeals Officer review after a rejection.",
  },
  {
    type: "Unfiled return preparation (per year)",
    total: "1 – 4 weeks",
    phases: "Records gathering is usually the bottleneck. Wage-and-income transcripts from IRS take ~1 week.",
  },
  {
    type: "IRS correspondence audit",
    total: "3 – 6 months",
    phases: "30-day response → IRS review → potential second exchange → Letter 525 (30-day) or 90-day letter.",
  },
  {
    type: "IRS office or field audit",
    total: "6 – 18 months",
    phases: "Initial IDR → field work (3–6 mo) → proposed adjustments → manager conference or Appeals.",
  },
  {
    type: "IRS Appeals",
    total: "6 – 12 months",
    phases: "Protest letter → Appeals Officer assignment → conference → settlement or Tax Court petition.",
  },
  {
    type: "Payroll tax (941) workout",
    total: "4 – 12 months",
    phases: "Revenue Officer engagement, in-business trust fund determination, often paired with TFRP defense.",
  },
  {
    type: "Trust Fund Recovery Penalty defense",
    total: "3 – 9 months",
    phases: "Form 4180 interview prep → interview → proposed assessment → protest to Appeals.",
  },
];

const accelerators = [
  "File Form 2848 Power of Attorney on day one — the IRS will route everything through your CPA, eliminating taxpayer-side miscommunication.",
  "Pull IRS transcripts before the first negotiation call. Knowing assessed balances, CSEDs, and missing returns prevents wasted cycles.",
  "Get into compliance first. The IRS will not negotiate a long-term resolution while returns are unfiled or current-year estimated payments are missed.",
  "Submit complete Form 433 financials the first time. Incomplete packages trigger IRS verification requests that add 30–60 days.",
  "Respond to IRS deadlines on day one of the window, not the last. ACS hold queues are shorter early in the month.",
];

const delayers = [
  "Missing tax returns the IRS hasn't yet identified as missing.",
  "A Revenue Officer (RO) assignment — slower than ACS in some cases, faster in others, but always more paperwork.",
  "Foreign income, cryptocurrency, or complex pass-through entities.",
  "Pending audits or unresolved Appeals on the same balance.",
  "IRS service-wide processing backlogs (worst during peak filing season, Jan–Apr).",
  "Bankruptcy filings, which automatically toll collection statutes and pause IRS action.",
];

const HowLongDoesTaxResolutionTake = () => {
  const jsonLd: object[] = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "How Long Does IRS Tax Resolution Take? Realistic Timelines by Case Type (2026)",
      description: QUICK_ANSWER.slice(0, 200),
      datePublished,
      dateModified: lastReviewed,
      author: {
        "@type": "Person",
        name: founder.name,
        jobTitle: founder.title,
        url: `${brand.siteUrl}/about/franklin-sofi`,
      },
      reviewedBy: {
        "@type": "Person",
        name: founder.name,
        jobTitle: founder.title,
        url: `${brand.siteUrl}/about/franklin-sofi`,
      },
      publisher: {
        "@type": "Organization",
        name: brand.legalName,
        url: brand.siteUrl,
      },
      mainEntityOfPage: `${brand.siteUrl}/resources/how-long-does-tax-resolution-take`,
      inLanguage: "en-US",
      speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: [".speakable", "h1", "h2"],
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "How long does it take to resolve an IRS tax debt?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Most IRS tax resolution cases close in 3 to 9 months. Streamlined installment agreements can be set up the same day. Offers in Compromise typically take 6 to 12 months, and longer if appealed. Wage garnishment and bank levy releases can be obtained within 24 to 72 hours.",
          },
        },
        {
          "@type": "Question",
          name: "How long does an Offer in Compromise take?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "A lump-sum OIC typically takes 6 to 9 months from submission to IRS decision. Periodic-payment OICs take 9 to 14 months. Appealed OICs add 3 to 9 months for Appeals review, pushing total time to 12–24 months.",
          },
        },
        {
          "@type": "Question",
          name: "How fast can a wage garnishment be released?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "A wage garnishment can usually be released within 24 to 72 hours once a CPA files Form 2848 Power of Attorney, submits a Form 433-F financial statement, and proposes a resolution (installment agreement, CNC, or OIC) to the IRS.",
          },
        },
        {
          "@type": "Question",
          name: "How long does an IRS audit take?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Correspondence audits typically take 3 to 6 months. Office and field audits take 6 to 18 months. Audits that go to Appeals add another 6 to 12 months on top of the exam timeline.",
          },
        },
        {
          "@type": "Question",
          name: "Why is my IRS case taking so long?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Common causes of delay: unfiled returns the IRS hasn't yet flagged, incomplete Form 433 financial disclosures, Revenue Officer assignment, complex assets or foreign income, and IRS service-wide processing backlogs during peak filing season (Jan–Apr).",
          },
        },
      ],
    },
  ];

  return (
    <SiteLayout>
      <PageSEO
        title="How Long Does IRS Tax Resolution Take? Realistic Timelines"
        description="A licensed CPA's realistic timelines for every IRS resolution case — installment agreements, OICs, audits, levy releases, payroll tax — plus what accelerates and delays them."
        path="/resources/how-long-does-tax-resolution-take"
        image={stockImages.penaltyClock.src}
        ogType="article"
        author={founder.name}
        publishedTime={datePublished}
        modifiedTime={lastReviewed}
        breadcrumbs={[
          { name: "Home", href: "/" },
          { name: "Resources", href: "/resources" },
          { name: "Tax Resolution Timeline", href: "/resources/how-long-does-tax-resolution-take" },
        ]}
        jsonLd={jsonLd}
        speakableSelectors={[".speakable", ".faq-question", ".faq-answer"]}
      />

      <ResourceHero
        breadcrumbs={[
          { name: "Home", href: "/" },
          { name: "Resources", href: "/resources" },
          { name: "Tax Resolution Timeline" },
        ]}
        eyebrow="Realistic timelines"
        title="How long does IRS tax resolution take?"
        summary="From a 24-hour wage levy release to a 14-month Offer in Compromise — honest timelines for every case type, plus the five moves that speed your case up and the six things that slow it down."
        facts={[
          { label: "Last reviewed", value: "Jun 2026" },
          { label: "Reviewed by", value: founder.name },
          { label: "Read time", value: "7 min" },
        ]}
      />

      <section className="py-12 lg:py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <QuickAnswer
            heading="How long does IRS tax resolution actually take?"
            answer={QUICK_ANSWER}
          />

          <ReviewedBy date={lastReviewed} />

          {/* Timeline table */}
          <h2 className="font-heading text-3xl font-bold text-navy-dark mt-12 mb-2">
            Realistic timelines by case type
          </h2>
          <p className="text-[15px] text-navy-dark/70 mb-6">
            These ranges reflect cases handled by a credentialed CPA with timely client cooperation. Self-represented timelines are typically 2–3× longer because the IRS responds more slowly to taxpayers without representation.
          </p>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-secondary/60 text-left">
                <tr>
                  <th className="p-4 font-semibold text-navy-dark">Case type</th>
                  <th className="p-4 font-semibold text-navy-dark whitespace-nowrap">Typical time</th>
                  <th className="p-4 font-semibold text-navy-dark">Phases</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {timelineRows.map((row) => (
                  <tr key={row.type} className="align-top">
                    <td className="p-4 font-semibold text-navy-dark">{row.type}</td>
                    <td className="p-4 text-gold-dark font-semibold whitespace-nowrap">{row.total}</td>
                    <td className="p-4 text-navy-dark/85">{row.phases}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Anatomy of a typical case */}
          <h2 className="font-heading text-3xl font-bold text-navy-dark mt-16 mb-4">
            Anatomy of a typical resolution case
          </h2>
          <p className="text-[16px] text-navy-dark/85 leading-relaxed mb-6">
            Whether the goal is an installment agreement, CNC, or OIC, almost every case follows the same five-phase arc:
          </p>
          <ol className="space-y-4">
            {[
              { phase: "Phase 1 — Intake & POA (Week 1)", body: "Form 2848 Power of Attorney filed with CAF unit; engagement letter signed; IRS transcripts pulled (account, wage-and-income, record-of-account for every open year)." },
              { phase: "Phase 2 — Compliance check (Weeks 1–4)", body: "Identify and prepare any unfiled returns; ensure current-year estimated tax payments or federal tax deposits are current. The IRS will not negotiate a resolution while filings are missing." },
              { phase: "Phase 3 — Financial analysis (Weeks 2–6)", body: "Form 433-A, 433-F, or 433-A(OIC) preparation; bank statement reconciliation; ALE (Allowable Living Expense) analysis; Reasonable Collection Potential computation." },
              { phase: "Phase 4 — IRS negotiation (Weeks 4–24)", body: "ACS calls or Revenue Officer meetings; resolution proposal (IA, CNC, OIC, or partial-pay IA); manager involvement if needed." },
              { phase: "Phase 5 — Implementation & monitoring", body: "Resolution put in writing; first payments processed; CSED tracking; annual compliance review." },
            ].map((p, i) => (
              <li key={p.phase} className="flex gap-4 rounded-xl border border-border bg-white p-5">
                <div className="flex-shrink-0 w-9 h-9 rounded-full bg-gold/20 text-gold-dark font-bold flex items-center justify-center">{i + 1}</div>
                <div>
                  <p className="m-0 font-semibold text-navy-dark">{p.phase}</p>
                  <p className="m-0 mt-1 text-[15px] text-navy-dark/85 leading-relaxed">{p.body}</p>
                </div>
              </li>
            ))}
          </ol>

          {/* Accelerators / delayers */}
          <div className="grid md:grid-cols-2 gap-6 mt-16">
            <div className="rounded-2xl border-2 border-gold/40 bg-gold/5 p-6 lg:p-8">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 className="w-6 h-6 text-gold-dark" />
                <h2 className="font-heading text-xl font-bold text-navy-dark m-0">
                  What speeds your case up
                </h2>
              </div>
              <ul className="space-y-2.5 mt-3">
                {accelerators.map((a) => (
                  <li key={a} className="flex gap-2 text-[15px] text-navy-dark/85 leading-relaxed">
                    <Clock className="w-4 h-4 text-gold-dark mt-1 flex-shrink-0" /><span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border-2 border-destructive/30 bg-destructive/5 p-6 lg:p-8">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-6 h-6 text-destructive" />
                <h2 className="font-heading text-xl font-bold text-navy-dark m-0">
                  What slows your case down
                </h2>
              </div>
              <ul className="space-y-2.5 mt-3">
                {delayers.map((d) => (
                  <li key={d} className="flex gap-2 text-[15px] text-navy-dark/85 leading-relaxed">
                    <Calendar className="w-4 h-4 text-destructive mt-1 flex-shrink-0" /><span>{d}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* IRS statute on collection (CSED) */}
          <h2 className="font-heading text-3xl font-bold text-navy-dark mt-16 mb-4">
            The 10-year collection clock (CSED)
          </h2>
          <p className="text-[16px] text-navy-dark/85 leading-relaxed">
            The IRS has 10 years from the date of assessment to collect a tax liability — the Collection Statute Expiration Date (CSED). Several events <em>toll</em> (pause) the CSED, extending the IRS's collection window:
          </p>
          <ul className="space-y-2 mt-3 text-[15px] text-navy-dark/85">
            <li className="flex gap-2.5"><CheckCircle2 className="w-4 h-4 text-gold-dark mt-1 flex-shrink-0" /><span>Pending Offer in Compromise (+ 30 days)</span></li>
            <li className="flex gap-2.5"><CheckCircle2 className="w-4 h-4 text-gold-dark mt-1 flex-shrink-0" /><span>Pending installment agreement (+ 30 days)</span></li>
            <li className="flex gap-2.5"><CheckCircle2 className="w-4 h-4 text-gold-dark mt-1 flex-shrink-0" /><span>CDP hearing request (+ 30 days)</span></li>
            <li className="flex gap-2.5"><CheckCircle2 className="w-4 h-4 text-gold-dark mt-1 flex-shrink-0" /><span>Bankruptcy filing (+ 6 months)</span></li>
            <li className="flex gap-2.5"><CheckCircle2 className="w-4 h-4 text-gold-dark mt-1 flex-shrink-0" /><span>Time outside the U.S. for more than 6 months continuously</span></li>
          </ul>
          <p className="text-[16px] text-navy-dark/85 leading-relaxed mt-4">
            Knowing every CSED on your account before choosing a strategy is critical. Sometimes the right move is to <strong>not</strong> file an OIC because the tolling would extend the clock by 12+ months on a liability that's about to expire on its own.
          </p>

          {/* Bottom line */}
          <h2 className="font-heading text-3xl font-bold text-navy-dark mt-16 mb-4">
            The bottom line
          </h2>
          <p className="text-[16px] text-navy-dark/85 leading-relaxed">
            Fast resolutions exist — but they require a credentialed practitioner working from day one, complete IRS transcripts, and current compliance. Any firm that promises a 30-day OIC or a same-day audit settlement is either inexperienced or dishonest. The honest answer to <em>"how long will this take?"</em> is <em>"it depends, and here's exactly what it depends on."</em>
          </p>

          {/* Related CTA */}
          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            <Link to="/resources/how-much-does-tax-resolution-cost" className="group flex items-center justify-between rounded-xl border border-border bg-white p-5 hover:border-gold hover:shadow-md transition">
              <span className="font-semibold text-navy-dark">How much does it cost?</span>
              <ArrowRight className="w-4 h-4 text-gold-dark group-hover:translate-x-1 transition" />
            </Link>
            <Link to="/services" className="group flex items-center justify-between rounded-xl border border-border bg-white p-5 hover:border-gold hover:shadow-md transition">
              <span className="font-semibold text-navy-dark">Browse resolution services</span>
              <ArrowRight className="w-4 h-4 text-gold-dark group-hover:translate-x-1 transition" />
            </Link>
          </div>

          <div className="mt-8 rounded-xl bg-navy-dark text-white p-6 lg:p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold mb-1">
                Get a realistic timeline for your case
              </p>
              <p className="m-0 text-white/90">
                We'll pull your IRS transcripts and tell you what to expect — for free.
              </p>
            </div>
            <a
              href={`tel:${brand.phoneTel}`}
              className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-light text-navy-dark font-bold px-6 py-3 rounded-lg whitespace-nowrap"
            >
              <Phone className="w-4 h-4" /> {brand.phone}
            </a>
          </div>
        </div>
      </section>

      <CTASection />
    </SiteLayout>
  );
};

export default HowLongDoesTaxResolutionTake;
